import { DatePipe } from '@angular/common';
import { Component, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { GetContentDto, GetContentResp, PromoObject } from '../../../@core/usecase/content/get-content.interface';
import { ContentLandingComponent } from './component/content-landing/content-landing.component';
import { PeriodLandingComponent } from './component/period-landing/period-landing.component';
import { TncLandingComponent } from './component/tnc-landing/tnc-landing.component';
import { TagListComponent } from './component/tag-list/tag-list.component';
import { Title } from '@angular/platform-browser';
import { DialogService } from '@shared/component.dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { PromoExpiredComponent } from './component/promo-expired/promo-expired.component';
import { CheckOffersComponent } from './component/check-offers/check-offers.component';
import { PromoType } from '@data/domain/promo.entity';
import { GetContentUsecase } from '@usecase/content/get-content.usecase';
import { take } from 'rxjs';
import { ITEM } from '@utils/constants';
import { DateTimePipe } from '@shared/pipes/date-time/date-time.pipe';
import { LoadingService } from '@shared/service.loading';
import { GtmTrackingService } from '../../shared/services/gtm-tracking.service';
import { WorldCupBannerComponent } from './component/world-cup-banner/world-cup-banner.component';
import { WorldCupScheduleComponent } from './component/world-cup-schedule/world-cup-schedule.component';
import { CountdownComponent } from './component/countdown/countdown.component';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [
    TranslateModule,
    FormsModule,
    NgxSkeletonLoaderModule,
    ContentLandingComponent,
    PeriodLandingComponent,
    TncLandingComponent,
    TagListComponent,
    PromoExpiredComponent,
    CheckOffersComponent,
    DateTimePipe,
    WorldCupBannerComponent,
    WorldCupScheduleComponent,
    CountdownComponent
],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss',
  providers: [
    DatePipe
  ]
})

export class LandingComponent implements OnInit {
  getContentData = signal<PromoObject>({
    ML: '',
    image: '',
    subcategory: '',
    page_title: '',
    title: '',
    slug: '',
    description: '',
    button_text: '',
    offering_text: '',
    start_date: '',
    end_date: '',
    terms_title: '',
    terms: '',
    load_text: '',
    hide_text: '',
    status: '',
    tags: [],
    period_text: '',
    end_time: '',
    start_time: ''
  });
  currentLang = signal<'id' | 'en'>('id');
  isPromoExpired = signal<boolean>(false);
  isClaimVoucher = signal<boolean>(false);
  msisdn = signal<string>('');
  promoType = signal<PromoType>('promo');
  isLoading = signal<boolean>(true);

  contentType!: string;
  categoryContent?: string;

  platformId = inject(PLATFORM_ID);
  router = inject(Router);
  public dialog = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly titleService = inject(Title);
  private readonly route = inject(ActivatedRoute);
  private readonly loadingService = inject(LoadingService);
  private readonly getContentUsecase = inject(GetContentUsecase);
  private readonly gtmTrackingService = inject(GtmTrackingService);

  ngOnInit() {
    this.handleGetParams();
    this.handleLangChange();
    this.trackPageLoad();
  }

  trackPageLoad() {
    // Track page load event for promo landing page using helper service
    this.gtmTrackingService.trackPageLoad(
      'Promo Landing',
      this.contentType || 'Unknown'
    );
  }

  handleGetParams() {
    this.route.params.subscribe(params => {
      this.contentType = params['content'];
    });
  }

  updateMetaTags(title: string) {
    this.titleService.setTitle(`${title} | Telkomsel`);
  }

  getContent() {
    this.loadingService?.show();
    this.isLoading.set(true);
    const payload: GetContentDto = {
      data: this.contentType
    };
    this.getContentUsecase.execute(payload).pipe(take(ITEM.SINGLE_EMISSION)).subscribe({
      next: (resp: GetContentResp) => {
          this.promoType.set(resp.typePromo as PromoType);
          this.categoryContent = resp.category;
          this.getContentData.set(resp.results[this.currentLang()]);
          this.isPromoExpired.set(resp.isEnded);
          this.updateMetaTags(this.getContentData().title);
          this.loadingService?.hide();
          this.isLoading.set(false);
          localStorage.setItem('promoType', resp.typePromo); // USED FOR OTHER COMPONENTS
          localStorage.setItem('mlId', this.getContentData().ML);
          this.trackPromoView();
      },
      error: () => {
        this.router.navigate(['404']);
      },
      complete: () => {
        this.loadingService?.hide();
      }
    });
  }

  trackPromoView() {
    // Track promo view event using helper service
    const promoData = this.getContentData();
    this.gtmTrackingService.trackPromoView({
      id: promoData.ML || this.contentType,
      name: promoData.title,
      category: this.categoryContent || 'promo',
      type: this.promoType()
    });
  }

  handleLangChange() {
    this.currentLang.set(this.translateService.currentLang as 'id' | 'en');

    // destroy subscription
    this.translateService.onLangChange.subscribe((res) => {
      this.currentLang.set(res.lang as 'id' | 'en');
      this.getContent();
    });
  }

  handleFormatDate(date: Date) {
    // pake shared formatter
    const MONTH_INDEX_OFFSET = 1; // MONTHS ARE 0-INDEXED, SO ADD 1
    const PADDING_LENGTH = 2;
    const PADDING_CHAR = '0';

    const month = String(date.getMonth() + MONTH_INDEX_OFFSET).padStart(PADDING_LENGTH, PADDING_CHAR);
    const day = String(date.getDate()).padStart(PADDING_LENGTH, PADDING_CHAR);
    const year = date.getFullYear();

    return `${month}-${day}-${year}`; //MM-DD-YYYY
  }

  validateIsClaimVoucher(msisdn: string) {
    if (msisdn && msisdn !== '') {
      this.isClaimVoucher.set(true);
      this.isPromoExpired.set(false);
    }
  }

  // GTM tracking methods for child components
  trackPromoInteraction(action: string, label?: string) {
    this.gtmTrackingService.trackPromoInteraction(action, label || this.getContentData().title);
  }

}
